import React from 'react'
    import { Link } from 'react-router-dom'
    import { ArrowRight, Bot, Database, Brain, Shield, Zap, CheckCircle, Star, TrendingUp, Users, Award } from 'lucide-react'

    const Home = () => {
      const features = [
        {
          icon: <Bot className="h-8 w-8 text-blue-600" />,
          title: "AI Agent Development",
          description: "Custom autonomous agents that handle complex business processes with minimal human intervention."
        },
        {
          icon: <Database className="h-8 w-8 text-purple-600" />,
          title: "Data Engineering",
          description: "Robust data pipelines and architectures that create the foundation for reliable AI systems."
        },
        {
          icon: <Brain className="h-8 w-8 text-indigo-600" />,
          title: "Generative AI Integration",
          description: "Harness the power of LLMs and RAG systems to reason and act on your proprietary data."
        },
        {
          icon: <Shield className="h-8 w-8 text-green-600" />,
          title: "Secure & Compliant",
          description: "Enterprise-grade security with HIPAA, GDPR, and industry-specific compliance built-in."
        }
      ]

      const industries = [
        {
          name: "Healthcare & Life Sciences",
          description: "AI agents for prior authorization, claims processing, and member engagement within HIPAA-compliant frameworks.",
          image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop"
        },
        {
          name: "Financial Services",
          description: "Autonomous systems for financial crime detection, compliance monitoring, and risk assessment.",
          image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop"
        },
        {
          name: "Enterprise Solutions",
          description: "Intelligent workflows and process automation that adapt and learn over time.",
          image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop"
        }
      ]

      const testimonials = [
        {
          name: "Dr. Sarah Chen",
          title: "Chief Technology Officer",
          company: "MedCore Health Systems",
          image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
          quote: "Tulians's AI agents transformed our prior authorization process. What used to take 3-5 days now happens in under 2 hours with 99.7% accuracy. Our patient satisfaction scores have never been higher.",
          rating: 5,
          industry: "Healthcare"
        },
        {
          name: "Michael Rodriguez",
          title: "Head of Compliance",
          company: "Global Financial Partners",
          image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
          quote: "The autonomous compliance monitoring system from Tulians has revolutionized our AML operations. We've reduced false positives by 75% while maintaining 100% regulatory compliance across all jurisdictions.",
          rating: 5,
          industry: "Financial Services"
        },
        {
          name: "Jennifer Park",
          title: "VP of Operations",
          company: "TechFlow Enterprises",
          image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
          quote: "Tulians's intelligent workflow automation has increased our operational efficiency by 60%. Their team understood our complex requirements and delivered a solution that truly adapts and learns.",
          rating: 5,
          industry: "Enterprise"
        }
      ]

      const caseStudies = [
        {
          title: "Healthcare Claims Processing Automation",
          client: "Regional Health Insurance Provider",
          industry: "Healthcare & Life Sciences",
          challenge: "Manual claims processing was taking 5-7 business days, causing patient dissatisfaction and high operational costs. Error rates were at 12% due to manual data entry.",
          solution: "Deployed AI agents for automated claims intake, validation, and processing with real-time decision making and exception handling.",
          results: [
            { metric: "Processing Time", improvement: "80% reduction", detail: "From 5-7 days to 4-6 hours" },
            { metric: "Error Rate", improvement: "95% reduction", detail: "From 12% to 0.6%" },
            { metric: "Cost Savings", improvement: "$2.4M annually", detail: "Reduced staffing and operational costs" },
            { metric: "Patient Satisfaction", improvement: "45% increase", detail: "Faster claim resolution" }
          ],
          image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=400&fit=crop",
          timeline: "3 months implementation",
          roi: "340% ROI in first year"
        },
        {
          title: "Financial Crime Detection System",
          client: "International Banking Group",
          industry: "Financial Services",
          challenge: "Legacy AML system generated 10,000+ false positive alerts monthly, requiring 40 FTE analysts. Regulatory pressure was mounting due to missed suspicious activities.",
          solution: "Implemented multi-agent AI system for real-time transaction monitoring, intelligent watchlist screening, and automated case management with explainable AI.",
          results: [
            { metric: "False Positives", improvement: "70% reduction", detail: "From 10,000 to 3,000 monthly alerts" },
            { metric: "Detection Accuracy", improvement: "99.9% accuracy", detail: "Zero regulatory violations" },
            { metric: "Analyst Productivity", improvement: "3x improvement", detail: "Focus on high-value investigations" },
            { metric: "Compliance Cost", improvement: "$8M savings", detail: "Reduced staffing and penalties" }
          ],
          image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
          timeline: "6 months implementation",
          roi: "450% ROI in 18 months"
        },
        {
          title: "Enterprise Process Intelligence Platform",
          client: "Fortune 500 Manufacturing Company",
          industry: "Enterprise Solutions",
          challenge: "Disconnected systems across 15 global facilities led to inefficient workflows, data silos, and inability to optimize operations in real-time.",
          solution: "Built comprehensive process intelligence platform with AI agents managing supply chain, quality control, and predictive maintenance workflows.",
          results: [
            { metric: "Operational Efficiency", improvement: "60% increase", detail: "Streamlined cross-facility processes" },
            { metric: "Downtime Reduction", improvement: "40% decrease", detail: "Predictive maintenance implementation" },
            { metric: "Quality Improvements", improvement: "25% reduction", detail: "In defect rates across all facilities" },
            { metric: "Cost Optimization", improvement: "$12M savings", detail: "Annual operational cost reduction" }
          ],
          image: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=600&h=400&fit=crop",
          timeline: "8 months implementation",
          roi: "280% ROI in first year"
        }
      ]

      return (
        <div className="min-h-screen" data-component-path="src/pages/Home.jsx" data-component-line="1:4" data-component-id="src/pages/Home.jsx:1:4">
          {/* Hero Section */}
          <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20 overflow-hidden" data-component-path="src/pages/Home.jsx" data-component-line="3:6" data-component-id="src/pages/Home.jsx:3:6">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" data-component-path="src/pages/Home.jsx" data-component-line="4:8" data-component-id="src/pages/Home.jsx:4:8"></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-component-path="src/pages/Home.jsx" data-component-line="5:8" data-component-id="src/pages/Home.jsx:5:8">
              <div className="text-center animate-fade-in" data-component-path="src/pages/Home.jsx" data-component-line="6:10" data-component-id="src/pages/Home.jsx:6:10">
                <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6" data-component-path="src/pages/Home.jsx" data-component-line="7:12" data-component-id="src/pages/Home.jsx:7:12">
                  Engineering the <span className="gradient-text" data-component-path="src/pages/Home.jsx" data-component-line="8:14" data-component-id="src/pages/Home.jsx:8:14">Autonomous Enterprise</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed" data-component-path="src/pages/Home.jsx" data-component-line="10:12" data-component-id="src/pages/Home.jsx:10:12">
                  We build end-to-end intelligent systems—from foundational data to autonomous AI agents—that automate complexity and accelerate growth.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-component-path="src/pages/Home.jsx" data-component-line="13:12" data-component-id="src/pages/Home.jsx:13:12">
                  <Link to="/contact" className="btn-primary flex items-center space-x-2" data-component-path="src/pages/Home.jsx" data-component-line="14:14" data-component-id="src/pages/Home.jsx:14:14">
                    <span data-component-path="src/pages/Home.jsx" data-component-line="15:16" data-component-id="src/pages/Home.jsx:15:16">Start Your Journey</span>
                    <ArrowRight className="h-5 w-5" data-component-path="src/pages/Home.jsx" data-component-line="16:16" data-component-id="src/pages/Home.jsx:16:16" />
                  </Link>
                  <Link to="/services" className="btn-secondary" data-component-path="src/pages/Home.jsx" data-component-line="18:14" data-component-id="src/pages/Home.jsx:18:14">
                    Explore Services
                  </Link>
                </div>
              </div>
              
              {/* Hero Image */}
              <div className="mt-16 animate-slide-up" data-component-path="src/pages/Home.jsx" data-component-line="24:10" data-component-id="src/pages/Home.jsx:24:10">
                <img 
                  src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop" 
                  alt="AI and Autonomous Systems" 
                  className="w-full max-w-5xl mx-auto rounded-2xl shadow-2xl"
                  loading="lazy"
                  data-component-path="src/pages/Home.jsx" data-component-line="25:12" data-component-id="src/pages/Home.jsx:25:12"
                />
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-20 bg-white" data-component-path="src/pages/Home.jsx" data-component-line="34:6" data-component-id="src/pages/Home.jsx:34:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Home.jsx" data-component-line="35:8" data-component-id="src/pages/Home.jsx:35:8">
              <div className="text-center mb-16" data-component-path="src/pages/Home.jsx" data-component-line="36:10" data-component-id="src/pages/Home.jsx:36:10">
                <h2 className="text-4xl font-bold text-gray-900 mb-4" data-component-path="src/pages/Home.jsx" data-component-line="37:12" data-component-id="src/pages/Home.jsx:37:12">
                  Comprehensive Solutions for an Autonomous World
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-component-path="src/pages/Home.jsx" data-component-line="40:12" data-component-id="src/pages/Home.jsx:40:12">
                  We offer a full spectrum of services designed to turn your data into intelligent, automated action.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-component-path="src/pages/Home.jsx" data-component-line="45:10" data-component-id="src/pages/Home.jsx:45:10">
                {features.map((feature, index) => (
                  <div key={index} className="bg-white p-8 rounded-xl shadow-lg card-hover border border-gray-100" data-component-path="src/pages/Home.jsx" data-component-line="46:12" data-component-id="src/pages/Home.jsx:46:12">
                    <div className="mb-4" data-component-path="src/pages/Home.jsx" data-component-line="47:14" data-component-id="src/pages/Home.jsx:47:14">{feature.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3" data-component-path="src/pages/Home.jsx" data-component-line="48:14" data-component-id="src/pages/Home.jsx:48:14">{feature.title}</h3>
                    <p className="text-gray-600" data-component-path="src/pages/Home.jsx" data-component-line="49:14" data-component-id="src/pages/Home.jsx:49:14">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Client Testimonials Section */}
          <section className="py-20 bg-gray-50" data-component-path="src/pages/Home.jsx" data-component-line="55:6" data-component-id="src/pages/Home.jsx:55:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Home.jsx" data-component-line="56:8" data-component-id="src/pages/Home.jsx:56:8">
              <div className="text-center mb-16" data-component-path="src/pages/Home.jsx" data-component-line="57:10" data-component-id="src/pages/Home.jsx:57:10">
                <h2 className="text-4xl font-bold text-gray-900 mb-4" data-component-path="src/pages/Home.jsx" data-component-line="58:12" data-component-id="src/pages/Home.jsx:58:12">
                  Trusted by Industry Leaders
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-component-path="src/pages/Home.jsx" data-component-line="61:12" data-component-id="src/pages/Home.jsx:61:12">
                    See what our clients say about their transformation journey with Tulians' autonomous solutions.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-component-path="src/pages/Home.jsx" data-component-line="66:10" data-component-id="src/pages/Home.jsx:66:10">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white p-8 rounded-xl shadow-lg card-hover" data-component-path="src/pages/Home.jsx" data-component-line="67:12" data-component-id="src/pages/Home.jsx:67:12">
                    <div className="flex items-center mb-6" data-component-path="src/pages/Home.jsx" data-component-line="68:14" data-component-id="src/pages/Home.jsx:68:14">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full object-cover mr-4"
                        loading="lazy"
                        data-component-path="src/pages/Home.jsx" data-component-line="69:16" data-component-id="src/pages/Home.jsx:69:16"
                      />
                      <div data-component-path="src/pages/Home.jsx" data-component-line="75:16" data-component-id="src/pages/Home.jsx:75:16">
                        <h4 className="font-semibold text-gray-900" data-component-path="src/pages/Home.jsx" data-component-line="76:18" data-component-id="src/pages/Home.jsx:76:18">{testimonial.name}</h4>
                        <p className="text-sm text-gray-600" data-component-path="src/pages/Home.jsx" data-component-line="77:18" data-component-id="src/pages/Home.jsx:77:18">{testimonial.title}</p>
                        <p className="text-sm font-medium text-blue-600" data-component-path="src/pages/Home.jsx" data-component-line="78:18" data-component-id="src/pages/Home.jsx:78:18">{testimonial.company}</p>
                      </div>
                    </div>
                    
                    <div className="flex mb-4" data-component-path="src/pages/Home.jsx" data-component-line="82:14" data-component-id="src/pages/Home.jsx:82:14">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" data-component-path="src/pages/Home.jsx" data-component-line="83:16" data-component-id="src/pages/Home.jsx:83:16" />
                      ))}
                    </div>
                    
                    <blockquote className="text-gray-700 italic mb-4" data-component-path="src/pages/Home.jsx" data-component-line="87:14" data-component-id="src/pages/Home.jsx:87:14">
                      "{testimonial.quote}"
                    </blockquote>
                    
                    <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full" data-component-path="src/pages/Home.jsx" data-component-line="91:14" data-component-id="src/pages/Home.jsx:91:14">
                      {testimonial.industry}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>


          {/* Industries Section */}
          <section className="py-20 bg-gray-50" data-component-path="src/pages/Home.jsx" data-component-line="161:6" data-component-id="src/pages/Home.jsx:161:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Home.jsx" data-component-line="162:8" data-component-id="src/pages/Home.jsx:162:8">
              <div className="text-center mb-16" data-component-path="src/pages/Home.jsx" data-component-line="163:10" data-component-id="src/pages/Home.jsx:163:10">
                <h2 className="text-4xl font-bold text-gray-900 mb-4" data-component-path="src/pages/Home.jsx" data-component-line="164:12" data-component-id="src/pages/Home.jsx:164:12">
                  Industries We Serve
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-component-path="src/pages/Home.jsx" data-component-line="167:12" data-component-id="src/pages/Home.jsx:167:12">
                  Tailored intelligence for demanding sectors with deep domain expertise.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-component-path="src/pages/Home.jsx" data-component-line="172:10" data-component-id="src/pages/Home.jsx:172:10">
                {industries.map((industry, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden card-hover" data-component-path="src/pages/Home.jsx" data-component-line="173:12" data-component-id="src/pages/Home.jsx:173:12">
                    <img 
                      src={industry.image} 
                      alt={industry.name}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                      data-component-path="src/pages/Home.jsx" data-component-line="174:14" data-component-id="src/pages/Home.jsx:174:14"
                    />
                    <div className="p-6" data-component-path="src/pages/Home.jsx" data-component-line="180:14" data-component-id="src/pages/Home.jsx:180:14">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3" data-component-path="src/pages/Home.jsx" data-component-line="181:16" data-component-id="src/pages/Home.jsx:181:16">{industry.name}</h3>
                      <p className="text-gray-600" data-component-path="src/pages/Home.jsx" data-component-line="182:16" data-component-id="src/pages/Home.jsx:182:16">{industry.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600" data-component-path="src/pages/Home.jsx" data-component-line="190:6" data-component-id="src/pages/Home.jsx:190:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-component-path="src/pages/Home.jsx" data-component-line="191:8" data-component-id="src/pages/Home.jsx:191:8">
              <h2 className="text-4xl font-bold text-white mb-6" data-component-path="src/pages/Home.jsx" data-component-line="192:10" data-component-id="src/pages/Home.jsx:192:10">
                Ready to Build Your Autonomous Future?
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto" data-component-path="src/pages/Home.jsx" data-component-line="195:10" data-component-id="src/pages/Home.jsx:195:10">
                Let's schedule a discovery call to identify the first high-impact workflow you can automate.
              </p>
              <Link 
                to="/contact" 
                className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105"
                data-component-path="src/pages/Home.jsx" data-component-line="198:10" data-component-id="src/pages/Home.jsx:198:10"
              >
                <span data-component-path="src/pages/Home.jsx" data-component-line="202:12" data-component-id="src/pages/Home.jsx:202:12">Get Started Today</span>
                <ArrowRight className="h-5 w-5" data-component-path="src/pages/Home.jsx" data-component-line="203:12" data-component-id="src/pages/Home.jsx:203:12" />
              </Link>
            </div>
          </section>
        </div>
      )
    }

    export default Home